import React from 'react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { Box, Optional, Markdown } from '@honda-canada/design-system-react';
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import { useTranslation } from 'react-i18next';
import themeStyles from './Disclaimer.styles';
import getDropdownOptionsData, { optionsKeys } from '../../utils/getDropdownOptionsData';
import useDropdownOptions from '../../hooks/useDropdownOptions';
import { useFinance } from '../SelectProductsContext/SelectProductsProvider';
import { usePriceAndFinanceDetails } from '../PriceAndFinanceContext';

const Container = themeStyles.apply(Box, 'Container');
const DisclaimerElement = themeStyles.apply(Markdown, 'DisclaimerElement');

const Disclaimer = () => {
  const { isLoading, pricing, finance, isSellingProvince } = usePriceAndFinanceDetails();
  const paymentCalculator = finance;
  const pricingData = pricing;
  const { sitecoreContext } = useSitecoreContext() || {};
  const language = sitecoreContext?.language || 'en';
  const { t } = useTranslation();

  const { dropdownOptions } = useDropdownOptions();
  const [yearData, modelData] = getDropdownOptionsData(dropdownOptions, [optionsKeys.year, optionsKeys.model]);
  const [financeContext] = useFinance();
  const modelName = modelData?.text;
  const modelYear = yearData?.value;
  const paymentFrequency = financeContext?.options;

  if (!pricing || !finance) return null;

  let numberOfPayments;

  switch (paymentFrequency) {
    case 'weekly':
      numberOfPayments = paymentCalculator.weekly.noOfWeeklyPayments;
      break;
    case 'monthly':
      numberOfPayments = paymentCalculator.monthly.termOptionsDefault;
      break;
    case 'biweekly':
      numberOfPayments = paymentCalculator.biweekly.noOfBiweeklyPayments;
      break;
    default:
      numberOfPayments = null;
  }

  const freightPdi = paymentCalculator?.monthly?.freightPdi;
  const ppsaRegistrationFee = paymentCalculator?.monthly?.levies?.ppsaRegistrationFee?.value;
  const registeringAgentFee = paymentCalculator?.monthly?.levies?.registeringAgentFee?.value;
  const costOfBorrowing = paymentCalculator?.monthly?.totalCostOfBorrowing;
  const totalAmount = pricingData?.total;

  const disclaimerParams = {
    dateFrom: paymentCalculator?.monthly?.ratePeriodStart,
    dateTo: paymentCalculator?.monthly?.ratePeriodEnd,
    modelYear,
    modelName,
    sellingPrice: formatPrice(pricingData?.total, language, 2),
    msrp: formatPrice(pricingData?.value, language, 2),
    freight: formatPrice(freightPdi, 2),
    registrationFee: formatPrice(ppsaRegistrationFee || 0, language, 2),
    registeringFee: formatPrice(registeringAgentFee || 0, language, 2),
    downpayment: formatPrice(paymentCalculator?.monthly?.downPayment || 0, language, 2),
    financeApr: paymentCalculator?.monthly?.apr,
    weeklyPaymentAmt: formatPrice(paymentCalculator?.monthly?.total || 0, language, 2),
    months: paymentCalculator?.monthly?.termOptionsDefault,
    weeklyPayments: numberOfPayments,
    costOfBorrowing: formatPrice(costOfBorrowing, language, 2),
    totalObligation: formatPrice(totalAmount + costOfBorrowing, language, 2),
    frequency: paymentFrequency,
  };

  const dynamicDisclaimer = t('Shared.Common.paymentDisclaimerText', disclaimerParams);
  const staticDisclaimer = t('Shared.Common.paymentMsrpDisclaimerText', disclaimerParams);

  const sellingPriceStaticDisclaimers = t('Sub-apps.PriceAndFinance.paymentSummaryDisclaimerText');
  const msrpPriceStaticDisclaimers = t('Sub-apps.PriceAndFinance.paymentSummaryMsrpDisclaimerText');

  const disclaimerType = isSellingProvince ? 'cy-dynamic-disclaimer' : 'cy-static-disclaimer';
  const disclaimerContent = isSellingProvince ? dynamicDisclaimer : staticDisclaimer;
  const staticDisclaimersBlock = isSellingProvince ? sellingPriceStaticDisclaimers : msrpPriceStaticDisclaimers;

  return (
    <Optional when={!isLoading}>
      <Container data-testid="cy-disclaimer-container">
        <DisclaimerElement data-testid="cy-static-disclaimers-block">{staticDisclaimersBlock}</DisclaimerElement>
        <DisclaimerElement data-testid={disclaimerType}>{disclaimerContent}</DisclaimerElement>
      </Container>
    </Optional>
  );
};
export default Disclaimer;
